<script lang="ts">
	import { format, locale } from 'svelte-i18n';

	import type { PremiumBenefit } from 'src/types/Subscriptions';

	import BenefitsContent from './BenefitsContent.svelte';
	import BenefitBadges from './BenefitBadges.svelte';
	import { Col, Icon, Row, Text, Loader, Stack } from '@Shelly/shelly-design-system';

	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getPremiumBenefitsList,
		mapCmsItemsToPremiumBenefits,
	} from 'src/helpers/subscription_benefits_helpers';
	import { fetchBenefits } from 'src/services/changelog';

	let benefitsList: PremiumBenefit[] = [];
	let isLoading = true;
	let requestSeq = 0;

	$: standardColumnLabel = $format('label_standard', { default: 'Standard' });
	$: featuredColumnLabel = $format('text_premium_subheader_benefits_premium', {
		default: 'Premium',
	});

	const useFallback = () => {
		benefitsList = getPremiumBenefitsList($format);
	};

	const loadBenefits = async (currentLocale: string) => {
		const reqId = ++requestSeq;
		isLoading = true;

		try {
			const data = await fetchBenefits(currentLocale, 'premium');
			if (reqId !== requestSeq) return;

			if (data?.items.length) {
				benefitsList = mapCmsItemsToPremiumBenefits(data.items);
			} else {
				useFallback();
			}
		} catch {
			if (reqId !== requestSeq) return;
			useFallback();
		} finally {
			if (reqId === requestSeq) isLoading = false;
		}
	};

	/**
	 * Track the displayed UI locale rather than a persisted settings field.
	 * CMS translations must match the language the user is actually seeing.
	 */
	$: if ($locale) {
		loadBenefits($locale);
	}
</script>

{#if isLoading && benefitsList.length === 0}
	<div class="d-flex justify-content-center p-m">
		<Loader size="m" />
	</div>
{:else}
	<Stack gap="s">
		<Row class="benefit-table-header">
			<Col sm="6" md="8" class="benefit-table-header-cell">
				<Text noWrap styleType="body-small" class="text-color-muted text-bold text-uppercase">
					{$format('text_premium_subheader_benefits_what_you_get', {
						default: 'What you get',
					})}
				</Text>
			</Col>
			<Col sm="3" md="2" class="benefit-table-header-cell benefit-table-header-cell--plan">
				<Text
					noWrap
					styleType="body-small"
					class="benefit-header-label text-white text-bold text-uppercase text-center"
				>
					{standardColumnLabel}
				</Text>
				<span
					class="benefit-header-icon benefit-header-icon--standard"
					role="img"
					aria-label={standardColumnLabel}
				>
					<span class="benefit-header-icon-badge">
						<Icon icon="fak fa-solid-shelly" size="s" />
					</span>
				</span>
			</Col>
			<Col sm="3" md="2" class="benefit-table-header-cell benefit-table-header-cell--plan">
				<Text
					noWrap
					styleType="body-small"
					class="benefit-header-label icon-premium-color text-bold text-uppercase text-center"
				>
					{featuredColumnLabel}
				</Text>
				<span
					class="benefit-header-icon benefit-header-icon--premium"
					role="img"
					aria-label={featuredColumnLabel}
				>
					<span class="benefit-header-icon-badge">
						<Icon icon="fas fa-gem" size="s" class="icon-premium-color" />
					</span>
				</span>
			</Col>
		</Row>

		{#each benefitsList as benefit, index (benefit.text)}
			<Row>
				<Col sm="6" md="8">
					<div class="d-flex flex-direction-column">
						<div class="d-flex align-items-center flex-wrap mb-xs gap-xs">
							<Text class="text-bold" element="span" styleType="body-small">
								{capFirstChar(benefit.text)}
							</Text>
							<BenefitBadges
								isAI={benefit.isAI}
								isNew={benefit.new}
								betaFlag={benefit.betaFlag}
								comingSoon={benefit.comingSoon}
								isNotPremiumOnly={benefit.isNotPremiumOnly}
							/>
						</div>

						<Text class="text-color-muted" styleType="body-extra-small">
							{benefit.info}
						</Text>
					</div>
				</Col>
				<Col sm="3" md="2">
					<BenefitsContent benefitSubscription={benefit.basicSubscription} />
				</Col>
				<Col sm="3" md="2">
					<BenefitsContent benefitSubscription={benefit.premiumSubscription} isPremium />
				</Col>
			</Row>
			{#if index < benefitsList.length - 1}
				<hr class="hr-color-2" />
			{/if}
		{/each}
	</Stack>
{/if}

<style>
	:global(.benefit-table-header) {
		align-items: center;
	}

	:global(.benefit-table-header-cell) {
		display: flex;
		align-items: center;
		min-height: 40px;
	}

	:global(.benefit-table-header-cell--plan) {
		justify-content: center;
	}

	.benefit-header-icon {
		display: none;
		align-items: center;
		justify-content: center;
		width: 100%;
	}

	.benefit-header-icon--standard {
		color: var(--color-primary, #2491ff);
	}

	.benefit-header-icon-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		width: 36px;
		height: 36px;
		border: 1px solid transparent;
		border-radius: 50%;
	}

	.benefit-header-icon--standard .benefit-header-icon-badge {
		border-color: rgba(36, 145, 255, 0.42);
		background: linear-gradient(145deg, rgba(36, 145, 255, 0.24), rgba(36, 145, 255, 0.08));
		box-shadow: 0 8px 22px -12px rgba(36, 145, 255, 0.95);
	}

	.benefit-header-icon--premium .benefit-header-icon-badge {
		border-color: rgba(244, 224, 168, 0.48);
		background: linear-gradient(145deg, rgba(219, 183, 81, 0.24), rgba(255, 243, 207, 0.08));
		box-shadow: 0 8px 22px -12px rgba(219, 183, 81, 0.95);
	}

	@media screen and (max-width: 639px) {
		:global(.benefit-header-label) {
			display: none !important;
		}

		.benefit-header-icon {
			display: flex;
		}
	}
</style>
